import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum } from "./formatters";

// Phase 3 (M2): "Matrice pondérations" — pivot of tbl_key_destination joined
// with tbl_cost and tbl_key. Rows = cost accounts (Nr, Désignation, Clé).
// Columns = destinations (code + display name). Cells = ponderation, "-" when
// no destination row exists for that (cost, destination) pair.
//
// Layout notes:
//   - Landscape A4 (the client reference has 12 destination columns per page).
//   - When there are more destinations than fit on one page, split COLUMNS into
//     chunks and emit one A4 landscape page per chunk × row-pagination bucket.
//     Every page repeats the three fixed row-header columns (Nr / Désignation
//     / Clé) so the reader can scan across without re-scrolling to the left.
//   - Rows paginated at ROWS_PER_PAGE = 20 (deterministic 25px row height).
//
// Props
//   data              GET /repartition-report/ponderation-matrice
//                     → { columns: [{code, name}], rows: [{cost, designation,
//                       cle, values: [num|null,…]}] }
//   building, periodLabel, whiteLabelling, logoUrl, basePageNumber, grandTotalPages

// Pass #3 density: 20 → 22 to fill the empty space (chrome is now pinned so the
// landscape region is a constant ~607px; the matrice thead is 2 rows, so 22 body
// rows is the safe fill). MUST stay in lockstep with PONDERATION_ROWS_PER_PAGE in
// Preview.js or the "Page X/N" footer drifts.
const ROWS_PER_PAGE = 22;
const COLS_PER_PAGE = 12; // matches the client reference PDF (12 destinations)

const PonderationMatrice = ({
  data,
  building,
  periodLabel,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const columns = data?.columns || [];
  const rows = data?.rows || [];

  const colChunks = chunk(columns, COLS_PER_PAGE);
  const rowChunks = chunk(rows, ROWS_PER_PAGE);
  const pages = [];
  // Order: for each row-chunk, emit every column-chunk. Reader reads across
  // destinations for the current row-band first, then descends.
  for (let ri = 0; ri < Math.max(rowChunks.length, 1); ri += 1) {
    for (let ci = 0; ci < Math.max(colChunks.length, 1); ci += 1) {
      pages.push({
        rowsSlice: rowChunks[ri] || [],
        colsSlice: colChunks[ci] || [],
        colOffset: ci * COLS_PER_PAGE,
        pageIdx: pages.length,
      });
    }
  }
  const totalPages = Math.max(1, pages.length);

  return (
    <>
      {pages.map((p) => (
        <A4PageFrame
          key={p.pageIdx}
          building={building}
          periodLabel={periodLabel}
          sectionTitle={`Matrice pondérations${totalPages > 1 ? ` (${p.pageIdx + 1}/${totalPages})` : ""}`}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={basePageNumber + p.pageIdx}
          totalPages={grandTotalPages}
          landscape
        >
          <table style={tbl}>
            <colgroup>
              <col style={{ width: 55 }} />
              <col style={{ width: 240 }} />
              <col style={{ width: 90 }} />
              {p.colsSlice.map((_, i) => <col key={i} style={{ width: 58 }} />)}
            </colgroup>
            <thead>
              <tr style={{ borderBottom: "1px solid #00033F" }}>
                <th style={th} rowSpan={2}>Nr de compte</th>
                <th style={th} rowSpan={2}>Désignation du compte</th>
                <th style={th} rowSpan={2}>Clé</th>
                {p.colsSlice.map((c) => (
                  <th key={c.code} style={thNum}>{c.code}</th>
                ))}
              </tr>
              <tr style={{ borderBottom: "1px solid #00033F" }}>
                {p.colsSlice.map((c) => (
                  <th key={`${c.code}-name`} style={thNumSub} title={c.name}>
                    {truncate(c.name, 14)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {p.rowsSlice.map((r) => (
                <tr key={r.cost}>
                  <td style={td}>{r.cost}</td>
                  <td style={td} title={r.designation}>{r.designation}</td>
                  <td style={td}>{r.cle || ""}</td>
                  {p.colsSlice.map((_, ci) => {
                    const v = r.values[p.colOffset + ci];
                    return (
                      <td key={ci} style={tdNum}>{v == null ? "-" : fmtNum(v)}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </A4PageFrame>
      ))}
    </>
  );
};

function chunk(arr, size) {
  if (!arr || arr.length === 0) return [];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function truncate(s, n) {
  const str = String(s || "");
  return str.length <= n ? str : str.slice(0, n - 1) + "…";
}

// Same deterministic row-height recipe as SurfacesList/CostDetails — 5+14+5+1 = 25px
// so N rows × 25 + chrome fits comfortably in the 658px landscape content area.
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, tableLayout: "fixed" };
const th = { padding: "3px 4px", fontWeight: 600, textAlign: "left", fontSize: 9, whiteSpace: "nowrap", verticalAlign: "bottom" };
const thNum = { ...th, textAlign: "right" };
const thNumSub = { ...thNum, fontWeight: 400, fontSize: 8, color: "#4a4c6b" };
const td = {
  padding: "5px 4px",
  fontSize: 9,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default PonderationMatrice;
